import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import API_BASE_URL from "../../config";
import './PingMonitor.css';

const PingMonitor = () => {
  const [targets, setTargets] = useState('');
  const [status, setStatus] = useState('');
  const [logs, setLogs] = useState({});
  const [pingInfo, setPingInfo] = useState({});
  const eventSourcesRef = useRef({});
  const { t } = useTranslation();

  const handleStartPing = () => {
    const targetList = targets
      .split(',')
      .map(t => t.trim())
      .filter(Boolean);

    if (!targetList.length) {
      setStatus(t("please_enter_target"));
      return;
    }

    targetList.forEach(target => {
      if (eventSourcesRef.current[target]) return;


      const url = `${API_BASE_URL}/diagnostic-tools/ping-multi-stream?targets=${encodeURIComponent(target)}`;

      const es = new EventSource(url);
      eventSourcesRef.current[target] = es;

      es.onmessage = (event) => {
        try {
          const parts = event.data.split('|');
          const tgt = parts[0];
          const info = parts[1] || parts[0];

          const fullLogLine = `${tgt}: ${info}`;

          setLogs(prev => ({
            ...prev,
            [tgt]: [fullLogLine, ...(prev[tgt] || [])]
          }));

          const match = info.match(/time=([\d.]+)/);

          if (match) {
            const time = parseFloat(match[1]);

            setPingInfo(prev => ({
              ...prev,
              [tgt]: {
                min: prev[tgt]?.min
                  ? Math.min(prev[tgt].min, time)
                  : time,

                max: prev[tgt]?.max
                  ? Math.max(prev[tgt].max, time)
                  : time,

                packetLoss: prev[tgt]?.packetLoss ?? 0
              }
            }));
          }

        } catch (err) {
          console.error(`Error parsing ping data for ${target}:`, err);
        }
      };

      es.onerror = () => {
        console.error(`EventSource error for ${target}`);
        es.close();
        delete eventSourcesRef.current[target];
      };
    });

    setStatus(t("ping_started"));
  };

  const handleStopPing = () => {
    Object.values(eventSourcesRef.current).forEach(es => es.close());
    eventSourcesRef.current = {};
    setStatus(t("ping_stopped"));
  };

  const handleClearLogs = () => {
    setLogs({});
    setPingInfo({});
    setStatus(t("logs_cleared"));
  };

  const handleExportLogs = () => {
    const allLogs = Object.entries(logs)
      .map(([target, lines]) =>
        `--- ${target} ---\n${lines.slice().reverse().join('\n')}`
      )
      .join('\n\n');

    const blob = new Blob([allLogs], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = 'ping_logs.txt';
    a.click();

    URL.revokeObjectURL(url);
  };

  return (
    <div className="ping-container">

      <h2 className="ping-title">
        {t("live_ping_monitoring")}
      </h2>

      <div className="ping-input-area">

        <input
          type="text"
          value={targets}
          onChange={(e) => setTargets(e.target.value)}
          placeholder={t("enter_ips")}
          className="ping-ip-input"
        />

        <button className="ping-btn green" onClick={handleStartPing}>
          {t("start")}
        </button>

        <button className="ping-btn red" onClick={handleStopPing}>
          {t("stop")}
        </button>

        <button className="ping-btn yellow" onClick={handleClearLogs}>
          {t("clear_logs")}
        </button>

        <button className="ping-btn blue" onClick={handleExportLogs}>
          {t("export_logs")}
        </button>

      </div>

      <div className="ping-status">
        {status}
      </div>

      <div className="ping-logs-wrapper">

        {Object.keys(logs).map((target) => (
          <div key={target} className="log-box">

            <div className="ping-log-title">
              {target}

              <span className="ping-info">
                {t("min")}: {pingInfo[target]?.min ?? 'N/A'} ms |
                {t("max")}: {pingInfo[target]?.max ?? 'N/A'} ms |
                {t("packet_loss")}: {pingInfo[target]?.packetLoss ?? 'N/A'}%
              </span>

            </div>

            <div className="ping-log-body">

              {logs[target].slice(0, 20).map((line, idx) => {

                const isSuccess = line.includes('time=');
                const isError =
                  line.includes('timeout') ||
                  line.includes('failed');

                let textColor = '#cccccc';

                if (isSuccess) textColor = '#2ecc71';
                if (isError) textColor = '#ff4d4d';

                return (
                  <div
                    key={idx}
                    style={{
                      color: textColor,
                      fontFamily: 'monospace',
                      fontSize: '14px',
                      marginBottom: '2px'
                    }}
                  >
                    {line}
                  </div>
                );
              })}

            </div>

          </div>
        ))}

      </div>

    </div>
  );
};

export default PingMonitor;